import { Button, Moon, Sun, Settings as SettingsIcon } from "@app/ui";
import type { ThemeMode } from "@app/ui";
import { washStyle } from "../../features/block-wash.js";
import { BLOCK_TINT_DIFF_MAX, blockTintFromDiff } from "../../features/task-colors.js";
import type { I18nKey } from "../../lib/language.js";
import {
  fontStack,
  TEXT_FONT_DEFAULT,
  TEXT_FONTS,
  TEXT_SCALE_DEFAULT,
  TEXT_SCALE_MAX,
  TEXT_SCALE_MIN,
  type AppTextFont,
} from "../../lib/typography.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsStore, useThemeStore } from "../../stores/index.js";
import { SettingsCard } from "./SettingsCard.js";
import { SettingsSection } from "./SettingsSection.js";

const THEMES: { value: ThemeMode; labelKey: I18nKey; icon: typeof Sun }[] = [
  { value: "light", labelKey: "general.theme.light", icon: Sun },
  { value: "dark", labelKey: "general.theme.dark", icon: Moon },
  { value: "system", labelKey: "general.theme.system", icon: SettingsIcon },
];

const FONTS: { value: AppTextFont; labelKey: I18nKey }[] = TEXT_FONTS.map((value) => ({
  value,
  labelKey: `general.textFont.${value}` as I18nKey,
}));

/** "Appearance" — purely visual look: theme, block color, text size and font. */
export function AppearanceSettings() {
  const { t } = useI18n();
  const theme = useSettingsStore((s) => s.theme);
  const setTheme = useSettingsStore((s) => s.setTheme);
  const textScale = useSettingsStore((s) => s.textScale);
  const setTextScale = useSettingsStore((s) => s.setTextScale);
  const textFont = useSettingsStore((s) => s.textFont);
  const setTextFont = useSettingsStore((s) => s.setTextFont);
  // Block wash knobs live in the theme record (folded out of settings).
  const blockTintDiff = useThemeStore((s) => s.config.blockTintDiff);
  const setBlockTintDiff = useThemeStore((s) => s.setBlockTintDiff);
  const blockGradient = useThemeStore((s) => s.config.blockGradient);
  const setBlockGradient = useThemeStore((s) => s.setBlockGradient);

  const previewTint = blockTintFromDiff(blockTintDiff);
  const sample = "#3b82f6";
  const sampleTopic = "#a855f7";
  const taskPreview = washStyle({ priority: "none", custom_color: sample }, null, previewTint, blockGradient).vars;
  const eventPreview = washStyle({ priority: "none", custom_color: sample, is_event: true }, null, previewTint, blockGradient).vars;
  const mixPreview = washStyle({ priority: "none", custom_color: sample }, sampleTopic, previewTint, blockGradient).vars;

  return (
    <div className="flex flex-col gap-8">
      <SettingsSection title={t("appearance.display")}>
        <SettingsCard
          id="theme"
          title={t("general.theme")}
          onReset={() => void setTheme("system")}
        >
          <div className="flex gap-2">
            {THEMES.map(({ value, labelKey, icon: Icon }) => (
              <Button key={value} variant={theme === value ? "primary" : "outline"} onClick={() => void setTheme(value)}>
                <Icon className="h-4 w-4" /> {t(labelKey)}
              </Button>
            ))}
          </div>
        </SettingsCard>

        <SettingsCard
          id="block-color"
          title={t("general.blockColor")}
          description={t("general.blockColor.description")}
        >
          <div className="flex items-center gap-4">
            <input
              type="range"
              min={0}
              max={BLOCK_TINT_DIFF_MAX}
              step={0.01}
              value={blockTintDiff}
              onChange={(e) => void setBlockTintDiff(Number(e.target.value))}
              className="flex-1 accent-primary"
              aria-label={t("general.blockColor.aria")}
            />
            <div className="flex gap-2">
              <span className="task-wash rounded border px-2 py-1 text-xs" style={taskPreview}>{t("general.blockColor.task")}</span>
              <span className="task-wash rounded border px-2 py-1 text-xs" style={eventPreview}>{t("general.blockColor.event")}</span>
              <span className="task-wash rounded border px-2 py-1 text-xs" style={mixPreview}>{t("general.blockColor.mix")}</span>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{t("general.blockColor.gradient")}</span>
            <Button variant={blockGradient ? "primary" : "outline"} onClick={() => void setBlockGradient(true)}>{t("common.on")}</Button>
            <Button variant={!blockGradient ? "primary" : "outline"} onClick={() => void setBlockGradient(false)}>{t("common.off")}</Button>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("appearance.text")}>
        <SettingsCard
          id="text-size"
          title={t("general.textSize")}
          description={t("general.textSize.description")}
          onReset={() => void setTextScale(TEXT_SCALE_DEFAULT)}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground">{t("general.textSize.small")}</span>
              <input
                type="range"
                min={TEXT_SCALE_MIN}
                max={TEXT_SCALE_MAX}
                step={0.05}
                value={textScale}
                onChange={(e) => void setTextScale(Number(e.target.value))}
                className="flex-1 accent-primary"
                aria-label={t("general.textSize.aria")}
              />
              <span className="text-lg font-semibold">{t("general.textSize.large")}</span>
              <span className="w-12 text-right text-sm tabular-nums text-muted-foreground">{Math.round(textScale * 100)}%</span>
            </div>
            <div
              className="rounded-md border bg-muted/35 px-3 py-2"
              style={{ fontFamily: fontStack(textFont) }}
            >
              <p className="font-medium">{t("general.textSize.previewTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("general.textSize.previewBody")}</p>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard
          id="text-font"
          title={t("general.textFont")}
          description={t("general.textFont.description")}
          onReset={() => void setTextFont(TEXT_FONT_DEFAULT)}
        >
          <div className="flex flex-wrap gap-2">
            {FONTS.map((font) => (
              <Button
                key={font.value}
                variant={textFont === font.value ? "primary" : "outline"}
                onClick={() => void setTextFont(font.value)}
                style={{ fontFamily: fontStack(font.value) }}
              >
                <span className="text-base font-semibold">Aa</span>
                {t(font.labelKey)}
              </Button>
            ))}
          </div>
        </SettingsCard>
      </SettingsSection>
    </div>
  );
}
